import type { EChartsOption } from 'echarts';
import type { ChartData, TopItemsData } from '@/entities/dashboard';

import {
  formatDateCategory,
  formatWeekCategory,
  isDateCategories,
  resolveSeriesColors,
  sortChartDataByDate
} from './chart-data';

/**
 * A tooltip prints how much moved, not which way: outgoing stock arrives
 * negative and is drawn below the axis, and legacy RITA's tooltips dropped the
 * sign all the same (`Math.abs` in every dashboard formatter).
 */
const formatTooltipValue = (value: unknown): string => {
  const numeric = Number(value);

  return Number.isFinite(numeric)
    ? Math.abs(numeric).toLocaleString('en-US', { maximumFractionDigits: 1 })
    : String(value ?? '');
};

// Tooltip formatters return HTML that ECharts injects into the DOM, so any
// API-sourced text (category, series name, unit) must be escaped.
const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** One row of an `axis`-triggered tooltip, as ECharts hands it over. */
interface AxisTooltipParam {
  name?: string;
  seriesName?: string;
  marker?: string;
  value?: unknown;
}

/**
 * Tooltip for an `axis`-triggered chart: the hovered category, then one row per
 * series that has something to say about it.
 *
 * `trigger: 'axis'` hands over every series of the chart, whether or not it
 * reaches this category, so a chart with many stacked series — transport broken
 * down by origin, corridor or condition — prints a wall of `… : 0mt` around the
 * two rows that carry the data. Legacy RITA never showed those: its Highcharts
 * tooltips are per-point (`shared` left at its `false` default), and a segment
 * of zero height cannot be hovered. Dropping the zero rows restores that while
 * keeping what an axis tooltip is for — comparing the series that did move at
 * this category.
 *
 * The rows are hand-built HTML, so every piece of API-sourced text is escaped
 * (see `escapeHtml`).
 */
const createAxisTooltipFormatter =
  (unitLabel: string, headingText: (category: string) => string) =>
  (params: unknown): string => {
    if (!Array.isArray(params)) return '';

    const rows = params as AxisTooltipParam[];

    if (!rows.length) return '';

    const heading = `<div><strong>${escapeHtml(headingText(rows[0].name ?? ''))}</strong></div>`;
    const reported = rows.filter((row) => row.value !== 0);

    // A category every series sat out is on the axis on purpose since 353356 —
    // a date with no cargo is an answer, and one row is how it says so. Listing
    // the zero series instead would rebuild the very wall this formatter drops.
    if (!reported.length) {
      return `${heading}<div><strong>${escapeHtml(formatTooltipValue(0))}${escapeHtml(unitLabel || '')}</strong></div>`;
    }

    return reported.reduce(
      (html, row) =>
        `${html}<div>${row.marker ?? ''} ${escapeHtml(row.seriesName ?? '')}: <strong>${escapeHtml(formatTooltipValue(row.value))}${escapeHtml(unitLabel || '')}</strong></div>`,
      heading
    );
  };

const AXIS_LABEL_FONT_SIZE = 11;
const AXIS_LABEL_LINE_HEIGHT = 15;
const AXIS_LABEL_ROTATION = 45;
/**
 * Mean glyph width at `AXIS_LABEL_FONT_SIZE`, mixed-case Latin, in px — about
 * half the font size, the same ratio zrender's own width table averages to.
 */
const AXIS_LABEL_CHAR_WIDTH = 5.5;
/** Breathing room between one label and the next, px. */
const AXIS_LABEL_GAP = 4;
/**
 * Label box for an axis that has not been measured yet — wide enough to hold any
 * category, and present only so that a later state cannot leave its own, much
 * narrower, width behind in the merged option.
 */
const UNCONSTRAINED_LABEL_WIDTH = 1000;
/** Narrowest slot still worth wrapping a label into; below it the axis tilts. */
const WRAPPED_LABEL_MIN_SLOT = 55;
const WRAPPED_LABEL_MAX_LINES = 3;
/** Share of a wrapped line the text actually reaches before the next word wraps. */
const WRAPPED_LINE_FILL = 0.85;
/**
 * Marks a clipped label, in both the state ECharts clips for us and the one we
 * clip ourselves — ECharts' own default is three dots, which would have made the
 * two states disagree.
 */
const ELLIPSIS = '…';
/** Legacy RITA capped a tilted label at a third of the chart height. */
const TILTED_LABEL_HEIGHT_SHARE = 1 / 3;
/** …and a narrow chart cannot spare that, so the container caps it too. */
const TILTED_LABEL_WIDTH_SHARE = 0.25;
/**
 * …and a short label caps it at its own length, with room for glyphs wider than
 * the mean estimate. `containLabel` reserves the whole box, so a box sized from
 * the chart height left a band under the axis that grew with the chart (362932).
 */
const TILTED_LABEL_ESTIMATE_SLACK = 1.3;
/**
 * A zero-valued column is a rectangle of zero height, so a category no series
 * reached is blank canvas — indistinguishable from a category the filters never
 * returned, and since 354095 it has no tooltip either. This leaves a hairline in
 * the series colour there instead. Inside a stack that already carries data the
 * hairline is painted over by the segment above it, which is where a zero is
 * noise anyway — the same reason the tooltip drops it.
 */
const ZERO_BAR_MIN_HEIGHT = 2;
const STACK_EDGE_COLOR = '#666666';
/**
 * Breathing room between the chart and the left and right edges of its canvas, px.
 *
 * `containLabel` reserves the axis labels inside this, so it is the gap left over
 * once every label has its room — which is what makes it comparable to the top
 * and bottom, where the chart already runs to the edge. Percentages used to sit
 * here, and on a wide card they grew into a margin far bigger than the one above
 * and below.
 */
const GRID_MARGIN = 16;
/**
 * Room left of the y-axis labels for the axis name, px: one rotated line and a
 * little air. `containLabel` makes room for the labels only, so without this the
 * name sat half off the canvas.
 */
const Y_AXIS_NAME_ROOM = 20;
/** Room under the plot for the one-row legend, px. */
const LEGEND_ROOM = 32;
/** Width the y-axis labels take out of the plot, px. */
const Y_AXIS_LABEL_ALLOWANCE = 45;

export interface ChartSize {
  width: number;
  height: number;
}

export interface ChartViewport extends ChartSize {
  /**
   * Share of the categories the dataZoom window is showing, 0–1. Zooming in
   * hands the remaining categories more room each, which is as much a change of
   * available space as resizing the chart is.
   */
  visibleShare: number;
}

/** Stand-in until the container reports its size — see `useContainerSize`. */
const UNMEASURED: ChartViewport = { width: 0, height: 0, visibleShare: 1 };

const getResponsiveChartConfig = () => {
  const isMobile = window.innerWidth < 768;

  return {
    legend: {
      bottom: 0,
      type: 'scroll',
      pageIconSize: isMobile ? 12 : 15
    },
    grid: {
      bottom: LEGEND_ROOM,
      top: GRID_MARGIN
    }
  };
};

const estimateLabelWidth = (text: string): number => text.length * AXIS_LABEL_CHAR_WIDTH;

const keepWholeLabel = (value: string): string => value;

/**
 * How a category reads on the axis and in the tooltip heading. Dates stay ISO
 * inside the chart — sorting and gap-filling depend on it — and are only printed
 * the legacy way. A week reads as its whole span on the axis and in the tooltip,
 * as legacy labelled it: with the start day alone, a thinned-out axis of weeks
 * read as one grouped by quarter (360299).
 */
const getCategoryText = (data: ChartData) => {
  if (!isDateCategories(data.categories)) {
    return { axis: keepWholeLabel, tooltip: keepWholeLabel };
  }

  const text = data.weekly ? formatWeekCategory : formatDateCategory;

  return { axis: text, tooltip: text };
};

/** Greedy word wrap, using the estimate the layout decisions are taken on. */
const wrapLabel = (value: string, width: number): string[] => {
  const lines: string[] = [];
  let line = '';

  for (const word of value.split(/\s+/).filter(Boolean)) {
    const candidate = line ? `${line} ${word}` : word;

    if (!line || estimateLabelWidth(candidate) <= width) {
      line = candidate;
    } else {
      lines.push(line);
      line = word;
    }
  }

  if (line) {
    lines.push(line);
  }

  return lines;
};

/**
 * Cuts a label down to what its wrapped box can show, ending in an ellipsis.
 *
 * ECharts marks nothing when `lineOverflow` clamps a label: the text simply
 * stops mid-word, so "Christian Recovery and Development Agency" reads as an
 * organisation called "Christian Recovery and Developme". Only single-line
 * `overflow: 'truncate'` — what the tilted state uses — draws an ellipsis of its
 * own, so the wrapped state has to cut its own text.
 *
 * The lines are handed back with the breaks in them, so that where the text ends
 * and where the ellipsis sits are decided once, here, rather than measured one
 * way here and re-wrapped another way by ECharts. Wrapping happens against a
 * slightly narrower box than the real one (`WRAPPED_LINE_FILL`), so a name whose
 * glyphs run wider than the estimate cannot push its own ellipsis onto a fourth
 * line and back out of sight.
 */
const clampWrappedLabel = (value: string, width: number): string => {
  const lineWidth = width * WRAPPED_LINE_FILL;
  const lines = wrapLabel(value, lineWidth);

  if (lines.length <= WRAPPED_LABEL_MAX_LINES) {
    return value;
  }

  const kept = lines.slice(0, WRAPPED_LABEL_MAX_LINES);
  // The last line carries as much of the rest of the name as fits, so it reads
  // "and Devel…" rather than the "and…" a clean word break would leave.
  let last = [kept.pop(), ...lines.slice(WRAPPED_LABEL_MAX_LINES)].join(' ');

  while (last.length > 1 && estimateLabelWidth(`${last}${ELLIPSIS}`) > lineWidth) {
    last = last.slice(0, -1).trimEnd();
  }

  return [...kept, `${last}${ELLIPSIS}`].join('\n');
};

/** The y-axis title legacy put on every dashboard chart: `Weight (mt)`, or a bare count. */
const getYAxisName = (data: ChartData): string =>
  data.unitLabel ? `${data.metricLabel} (${data.unitLabel})` : data.metricLabel;

/**
 * The y axis titled with the measure, and the room left of its labels the title
 * needs. Category slots are measured on the narrowed plot, or label boxes would
 * outgrow their slots and `hideOverlap` would drop labels that fit.
 */
const getValueAxisLayout = (data: ChartData, viewport: ChartViewport) => {
  const name = getYAxisName(data);
  const nameRoom = name ? Y_AXIS_NAME_ROOM : 0;

  return {
    name,
    nameRoom,
    plotViewport: { ...viewport, width: viewport.width - nameRoom },
    axis: { name, nameLocation: 'middle' as const, nameMoveOverlap: true }
  };
};

/**
 * Room one category gets on the plot, px, or 0 before the chart is measured.
 *
 * Measured against the upright grid; a tilted axis insets the plot further,
 * which only makes a decision taken on it conservative. Only the categories
 * inside the dataZoom window compete for the plot. An unusable share reads as
 * "all of them" rather than poisoning the slot with a NaN, which would silently
 * drop every axis into the tilted branch.
 */
const categorySlot = (categories: string[], viewport: ChartViewport): number => {
  const plotWidth = viewport.width - 2 * GRID_MARGIN - Y_AXIS_LABEL_ALLOWANCE;

  if (!categories.length || plotWidth <= 0) return 0;

  const share = Number.isFinite(viewport.visibleShare) ? Math.min(Math.max(viewport.visibleShare, 0), 1) : 1;

  return plotWidth / Math.max(1, Math.round(categories.length * share));
};

/**
 * Narrowest slot, px, that still gets markers on the stock-on-hand area — legacy
 * Highcharts' rule: a 4px marker radius times its `enabledThreshold` of 2. Below
 * it the markers merge into a bead string along every band edge.
 */
const MARKER_MIN_SLOT = 8;

/**
 * X-axis labels for charts whose categories come from the *Group by* filter.
 *
 * Categories are free text from the API — organisation and location names run to
 * 40 characters, dates are short but there can be hundreds of them — so what
 * matters is not the text but how much room a single category gets. Legacy RITA
 * (Highcharts) took the same measurement and landed in one of three states,
 * which this reproduces:
 *
 * 1. the longest label fits its slot → upright, one line;
 * 2. it does not, but the slot is still wide enough to read a wrapped column →
 *    upright, wrapped, clipped after `WRAPPED_LABEL_MAX_LINES`;
 * 3. the slot is narrower than that → tilted 45° and clipped by width.
 *
 * Date and text categories go through the same rule: legacy tilted a year of
 * daily ticks and left a month of them upright, and so does this.
 *
 * Every category stays a candidate (`interval: 0`) and `hideOverlap` drops the
 * ones that would still collide, rather than letting them overlap. The tooltip
 * keeps the untruncated name in all three states.
 *
 * The grid reserves no room for any of it: `containLabel` measures whatever
 * comes back.
 */
const createCategoryAxisLayout = (
  categories: string[],
  viewport: ChartViewport,
  labelText: (category: string) => string
) => {
  /**
   * Every state fills in every one of these keys. `setOption` merges by default,
   * so a key one state leaves out keeps whatever the previous state put there —
   * which is how an axis that had tilted stayed tilted after growing wide enough
   * to sit upright, and how a wrapped axis dragged its line clamp into the
   * tilted state.
   */
  const uprightLabel = (width: number) => ({
    interval: 0,
    fontSize: AXIS_LABEL_FONT_SIZE,
    hideOverlap: true,
    rotate: 0,
    width,
    height: AXIS_LABEL_LINE_HEIGHT,
    // Pinned rather than left to the font: `height` is what clamps the wrapped
    // state, and the character budget behind its ellipsis counts the same lines.
    lineHeight: AXIS_LABEL_LINE_HEIGHT,
    overflow: 'none' as const,
    lineOverflow: 'truncate' as const,
    ellipsis: ELLIPSIS,
    formatter: labelText
  });

  const slot = categorySlot(categories, viewport);

  if (!slot) {
    return uprightLabel(UNCONSTRAINED_LABEL_WIDTH);
  }

  const labels = categories.map(labelText);
  const longestLabel = labels.reduce((widest, label) => Math.max(widest, estimateLabelWidth(label)), 0);

  // A box no wider than its own slot: `hideOverlap` compares boxes, not text, and
  // boxes wider than the slot all collide, leaving only the first label standing.
  if (longestLabel <= slot - AXIS_LABEL_GAP) {
    return uprightLabel(Math.floor(slot - AXIS_LABEL_GAP));
  }

  // Wrapping is only worth it while whole words still fit: a slot narrower than
  // the longest word leaves ECharts breaking words down the middle, which reads
  // as "Internati / onal / Rescue" and takes the ellipsis with it, because the
  // broken halves push the clipped line out of the clamp. Legacy tilted such an
  // axis rather than break its words, and so do we.
  const longestWord = labels.reduce(
    (widest, label) =>
      label.split(/\s+/).reduce((wordWidth, word) => Math.max(wordWidth, estimateLabelWidth(word)), widest),
    0
  );

  if (slot >= WRAPPED_LABEL_MIN_SLOT && longestWord <= slot * WRAPPED_LINE_FILL) {
    // The whole slot, not slot-minus-gap: wrapped text is centred in its box and
    // neighbours only touch.
    const boxWidth = Math.floor(slot);

    return {
      ...uprightLabel(boxWidth),
      overflow: 'break' as const,
      height: WRAPPED_LABEL_MAX_LINES * AXIS_LABEL_LINE_HEIGHT,
      // One slot-wide box per category tiles with its neighbours instead of
      // overlapping them, so nothing has to be dropped to keep the axis legible.
      hideOverlap: false,
      formatter: (value: string) => clampWrappedLabel(labelText(value), boxWidth)
    };
  }

  const maxWidth = Math.round(
    Math.min(
      viewport.height * TILTED_LABEL_HEIGHT_SHARE,
      viewport.width * TILTED_LABEL_WIDTH_SHARE,
      longestLabel * TILTED_LABEL_ESTIMATE_SLACK
    )
  );

  return {
    ...uprightLabel(maxWidth),
    rotate: AXIS_LABEL_ROTATION,
    overflow: 'truncate' as const
  };
};

export const createAreaChartOption = (rawData: ChartData, viewport: ChartViewport = UNMEASURED): EChartsOption => {
  const data = sortChartDataByDate(rawData);
  const seriesColors = resolveSeriesColors(data.series);
  const responsive = getResponsiveChartConfig();
  const categoryText = getCategoryText(data);
  const valueAxis = getValueAxisLayout(data, viewport);
  const categoryAxisLabel = createCategoryAxisLayout(data.categories, valueAxis.plotViewport, categoryText.axis);
  const tooltipRows = createAxisTooltipFormatter(data.unitLabel, categoryText.tooltip);
  const showSymbol = categorySlot(data.categories, valueAxis.plotViewport) >= MARKER_MIN_SLOT;

  return {
    title: {
      text: data.title,
      show: false,
      left: 'center',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold'
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          backgroundColor: '#6a7985'
        }
      },
      formatter: (params: unknown) => tooltipRows(Array.isArray(params) ? [...params].reverse() : params)
    },
    legend: {
      data: data.series.map((s) => s.name),
      ...responsive.legend
    },
    grid: {
      left: GRID_MARGIN + valueAxis.nameRoom,
      right: GRID_MARGIN,
      ...responsive.grid,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: data.categories,
      axisLabel: categoryAxisLabel,
      // The crosshair prints the raw category unless told otherwise.
      axisPointer: { label: { formatter: ({ value }: { value: unknown }) => categoryText.axis(String(value)) } }
    },
    yAxis: {
      type: 'value',
      ...valueAxis.axis,
      // Stock on hand cannot go below zero, but a band the API sums to -0.003
      // still pulled the axis down to the nice tick below zero. Legacy pins
      // `min: 0` on this axis, and only this one: stock movements, which do go
      // negative, are a column chart (361963).
      min: 0,
      axisLabel: {
        formatter: (value: number) => {
          if (Math.abs(value) >= 1000) {
            return (value / 1000).toFixed(1) + 'k';
          }
          return value.toString();
        }
      }
    },
    dataZoom: [
      {
        type: 'inside',
        start: 0,
        end: 100,
        xAxisIndex: 0
      }
    ],
    series: data.series
      .map((series, index) => ({ series, color: seriesColors[index] }))
      .reverse()
      .map(({ series, color }) => ({
        name: series.name,
        type: 'line',
        stack: series.stack || 'Total',
        stackStrategy: 'all',
        showSymbol,
        areaStyle: {
          opacity: 0.6
        },
        emphasis: {
          focus: 'series'
        },
        data: series.data,
        itemStyle: color ? { color } : undefined,
        lineStyle: {
          width: 2,
          color: STACK_EDGE_COLOR
        },
        smooth: true
      }))
  };
};

export const createColumnChartOption = (rawData: ChartData, viewport: ChartViewport = UNMEASURED): EChartsOption => {
  const data = sortChartDataByDate(rawData);
  const seriesColors = resolveSeriesColors(data.series);
  const responsive = getResponsiveChartConfig();
  const categoryText = getCategoryText(data);
  const valueAxis = getValueAxisLayout(data, viewport);
  const categoryAxisLabel = createCategoryAxisLayout(data.categories, valueAxis.plotViewport, categoryText.axis);

  return {
    title: {
      text: data.title,
      show: false,
      left: 'center',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold'
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      formatter: createAxisTooltipFormatter(data.unitLabel, categoryText.tooltip)
    },
    legend: {
      data: data.series.map((s) => s.name),
      ...responsive.legend
    },
    grid: {
      left: GRID_MARGIN + valueAxis.nameRoom,
      right: GRID_MARGIN,
      ...responsive.grid,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: data.categories,
      axisLabel: categoryAxisLabel
    },
    yAxis: {
      type: 'value',
      ...valueAxis.axis,
      axisLabel: {
        formatter: (value: number) => {
          if (Math.abs(value) >= 1000) {
            return (value / 1000).toFixed(1) + 'k';
          }
          return value.toString();
        }
      }
    },
    dataZoom: [
      {
        type: 'inside',
        start: 0,
        end: 100,
        xAxisIndex: 0
      }
    ],
    series: data.series.map((series, index) => ({
      name: series.name,
      type: 'bar',
      stack: series.stack,
      barMinHeight: ZERO_BAR_MIN_HEIGHT,
      data: series.data,
      itemStyle: seriesColors[index] ? { color: seriesColors[index] } : undefined
    }))
  };
};

export const createPieChartOption = (data: TopItemsData, title?: string): EChartsOption => {
  const isMobile = window.innerWidth < 768;

  return {
    title: title
      ? {
          text: title,
          left: 'center',
          textStyle: {
            fontSize: isMobile ? 14 : 16,
            fontWeight: 'normal'
          }
        }
      : undefined,
    tooltip: {
      trigger: 'item',
      formatter: '{b}: <strong>{c}%</strong>'
    },
    legend: {
      orient: isMobile ? 'horizontal' : 'vertical',
      left: isMobile ? 'center' : 'left',
      bottom: isMobile ? 0 : 'auto',
      type: isMobile ? 'scroll' : 'plain'
    },
    series: [
      {
        type: 'pie',
        radius: isMobile ? '40%' : '50%',
        center: isMobile ? ['50%', '45%'] : ['50%', '50%'],
        data: data.series.map((s) => ({
          value: s.percentage,
          name: s.name,
          itemStyle: { color: s.color }
        })),
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }
    ]
  };
};
